// BUILD-046 customer association: the "Not in the list? Open Pricing to add a customer if your Pricing role permits it." note below the Customer picker of the Assign task dialog.
//
// What it shows is decided by pricingLinkModel.ts (the server's `canAddCustomerInPricing` flag and the configured Pricing address); this file only draws it.
//   link : the conditional sentence opens <Pricing>/customers in a NEW tab (target=_blank, rel="noopener noreferrer"), so the Assign task dialog and
//          everything typed in it stay open. A small "Search again" button then asks the customer directory again, so a customer added in Pricing can be found;
//   text : the plain sentence when no link is offered (not a link; it does not say who can create a customer);
//   unavailable : the plain sentence saying Pricing access could not be checked right now (not a link, and not the "no access" sentence);
//   none : nothing.
import { PRICING_LINK_TEXT, PRICING_PLAIN_TEXT, PRICING_SEARCH_AGAIN_LABEL, PRICING_UNAVAILABLE_TEXT } from './pricingLinkModel';
import type { PricingNote as PricingNoteDecision } from './pricingLinkModel';

export interface PricingNoteProps {
  /** The id of the note, for the picker to point at with aria-describedby. */
  id: string;
  note: PricingNoteDecision;
  disabled?: boolean;
  /** The person followed the link (the host then searches again when the window is focused again). */
  onFollow: () => void;
  /** The "Search again" button: ask the customer directory again. */
  onSearchAgain: () => void;
}

export function PricingNote({ id, note, disabled = false, onFollow, onSearchAgain }: PricingNoteProps) {
  if (note.kind === 'none') return null;
  if (note.kind === 'text') {
    return (
      <p id={id} className="mt-1 break-words text-xs text-gray-600" data-testid="assign-pricing-note">
        {PRICING_PLAIN_TEXT}
      </p>
    );
  }
  if (note.kind === 'unavailable') {
    return (
      <p id={id} className="mt-1 break-words text-xs text-gray-600" data-testid="assign-pricing-note" data-note-kind="unavailable">
        {PRICING_UNAVAILABLE_TEXT}
      </p>
    );
  }
  return (
    <p id={id} className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-gray-600" data-testid="assign-pricing-note">
      <a
        href={note.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onFollow}
        className="break-words font-medium text-[#1f6a99] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
        data-testid="assign-pricing-link"
      >
        {PRICING_LINK_TEXT}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
      <button
        type="button"
        disabled={disabled}
        onClick={onSearchAgain}
        className="rounded text-xs text-[#1f6a99] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:text-gray-400"
        data-testid="assign-pricing-search-again"
      >
        {PRICING_SEARCH_AGAIN_LABEL}
      </button>
    </p>
  );
}
